import React, { JSX, useEffect, useState } from 'react';
import { ItemMaster } from '../../../../../../../../services/apiTypes';
import {
  Box,
  TableCell,
  Checkbox,
  TextField,
  Autocomplete,
  Select,
  MenuItem,
  Button,
  IconButton,
} from '@mui/material';
import { CheckOutlined, CloseOutlined, Language } from '@mui/icons-material';
import { manageItemStyle } from '../../../../../InspectionItemTable';
import { useTranslation } from 'react-i18next';
import { Options } from '../../../../../../ManageInspectionItem';
import { MaxLength } from '../../../../../../../../_constants/maxLengthConst';
import {
  ITEM_CONDITIONS,
  MANAGE_ITEM_MAX_R_CODE,
  MANAGE_ITEM_MODE,
} from '../../../../../../../../_constants/manageConst';

type EditDepth2RowProps = {
  item: ItemMaster;
  options: Options;
  mode: number;
  isAdmin: boolean;
  stickyPosition: number[];
  isDisabledBtn: boolean;
  handleInputChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    item: ItemMaster,
  ) => ItemMaster;
  checkInputForCondition: (item: ItemMaster) => boolean;
  onClickOk: (item: ItemMaster) => void;
  onClickCancel: () => void;
  onClickNamesEdit: (item: ItemMaster) => void;
  multiAutoCompleteMenu: (
    props: React.HTMLAttributes<HTMLLIElement> & {
      key: React.Key;
    },
    selected: boolean,
    itemName: string,
  ) => JSX.Element;
};

const EditDepth2Row: React.FC<EditDepth2RowProps> = ({
  item,
  options,
  mode,
  isAdmin,
  stickyPosition,
  isDisabledBtn,
  handleInputChange,
  checkInputForCondition,
  onClickOk,
  onClickCancel,
  onClickNamesEdit,
  multiAutoCompleteMenu,
}) => {
  const { t } = useTranslation();
  const [isRequiredCondition, setIsRequiredCondition] = useState(false);
  const [isDisabledSaveBtn, setIsDisabledSaveBtn] = useState(false);
  const [editItem, setEditItem] = useState(item);

  useEffect(() => {
    if (item.itemId) {
      controlInputCondition(item);
    }
  }, [item]);

  /** 判定条件の制御チェック */
  const controlInputCondition = (data: ItemMaster) => {
    const isNoCondition = checkInputForCondition(data);
    setIsRequiredCondition(!isNoCondition);
    if (isNoCondition) {
      // 判定条件不要
      setEditItem({ ...data, selectedConditions: '' });
    }
    setIsDisabledSaveBtn(!isNoCondition && !data.selectedConditions);
  };

  /** 入力中はこのコンポーネント内で変更を反映する */
  const onChangeInput = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const data = handleInputChange(e, editItem);
    setEditItem(data);
    // 判定条件の入力チェック
    controlInputCondition(data);
  };

  return (
    <React.Fragment>
      <TableCell
        sx={[manageItemStyle.leftSideSticky, { left: stickyPosition[4] }]}
      >
        <Box sx={manageItemStyle.emptyBox} />
      </TableCell>
      <TableCell
        sx={[manageItemStyle.leftSideSticky, { left: stickyPosition[5] }]}
      >
        <Box sx={{ display: 'flex' }}>
          <TextField
            key={`edit_displayName_${editItem.itemId}`}
            fullWidth
            size="small"
            value={editItem.displayName}
            name="displayName"
            sx={{ minWidth: '200px' }}
            onChange={(e) => onChangeInput(e)}
            onPointerDown={(e) => e.stopPropagation()}
            slotProps={{
              htmlInput: {
                maxLength: MaxLength.ManageInspectionItem.ITEM_NAME,
              },
            }}
          />
          {mode === MANAGE_ITEM_MODE.EDIT && (
            <IconButton
              sx={{ py: '8px' }}
              size="small"
              onClick={() => onClickNamesEdit(editItem)}
            >
              <Language fontSize="small" />
            </IconButton>
          )}
        </Box>
      </TableCell>
      {isAdmin && (
        <TableCell sx={manageItemStyle.iconCellCenter}>
          <Checkbox
            name="isOptionalAddition"
            checked={editItem.isOptionalAddition}
            onChange={(e) => {
              onChangeInput({
                target: {
                  name: 'isOptionalAddition',
                  value: e ? e.target.checked : '',
                },
              } as React.ChangeEvent<HTMLInputElement>);
            }}
            sx={{ py: '2px' }}
            onPointerDown={(e) => e.stopPropagation()}
          />
        </TableCell>
      )}
      <TableCell />
      <TableCell sx={{ py: 0, px: 0.5 }}>
        <Autocomplete
          size="small"
          sx={{ width: '120px' }}
          options={options.pCodeMasterList}
          noOptionsText={t('MSG_NO_OPTION')}
          getOptionLabel={(option) => option.code ?? ''}
          isOptionEqualToValue={(option, value) => option.code === value.code}
          value={
            options.pCodeMasterList.find(
              (code) => code.code === editItem.pCode,
            ) || null
          }
          onChange={(_, newValue) => {
            onChangeInput({
              target: {
                name: 'pCode',
                value: newValue ? newValue.code : '',
              },
            } as React.ChangeEvent<HTMLInputElement>);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              name="pCode"
              fullWidth
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
            />
          )}
          onPointerDown={(e) => e.stopPropagation()}
        />
      </TableCell>
      <TableCell sx={{ py: 0, px: 0.5 }}>
        <Autocomplete
          size="small"
          sx={{ width: '120px' }}
          options={options.fCodeMasterList}
          noOptionsText={t('MSG_NO_OPTION')}
          getOptionLabel={(option) => option.code ?? ''}
          isOptionEqualToValue={(option, value) => option.code === value.code}
          value={
            options.fCodeMasterList.find(
              (code) => code.code === editItem.fCode,
            ) || null
          }
          onChange={(_, newValue) => {
            onChangeInput({
              target: {
                name: 'fCode',
                value: newValue ? newValue.code : '',
              },
            } as React.ChangeEvent<HTMLInputElement>);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              name="fCode"
              fullWidth
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
            />
          )}
          onPointerDown={(e) => e.stopPropagation()}
        />
      </TableCell>
      <TableCell sx={{ py: 0, px: 0.5 }}>
        <Autocomplete
          multiple
          key="rCodeList"
          options={options.rCodeMasterList}
          noOptionsText={t('MSG_NO_OPTION')}
          size="small"
          sx={{ width: '300px', py: 1 }}
          disableCloseOnSelect
          getOptionLabel={(option) => option.code ?? ''}
          getOptionDisabled={() =>
            editItem.rCodeList.length === MANAGE_ITEM_MAX_R_CODE
          }
          value={
            options.rCodeMasterList.filter((rCode) =>
              editItem.rCodeList.some((r) => r === rCode.code),
            ) || null
          }
          onChange={(_, newValue) => {
            if (newValue.length <= MANAGE_ITEM_MAX_R_CODE) {
              onChangeInput({
                target: {
                  name: 'rCodeList',
                  value: newValue ? newValue.map((r) => r.code) : '',
                },
              } as React.ChangeEvent<HTMLInputElement>);
            }
          }}
          renderOption={(props, option, { selected }) =>
            multiAutoCompleteMenu(props, selected, option.code)
          }
          renderInput={(params) => (
            <TextField
              {...params}
              name="rCodeList"
              fullWidth
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
            />
          )}
        />
      </TableCell>
      <TableCell sx={{ py: 0, px: 0.5 }}>
        <Autocomplete
          multiple
          size="small"
          sx={{ py: 1 }}
          disableCloseOnSelect
          options={options.attribute1MasterList}
          noOptionsText={t('MSG_NO_OPTION')}
          getOptionLabel={(option) => option.name ?? ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={
            options.attribute1MasterList.filter((attr) =>
              editItem.selectedAttribute1.some((attr1) => attr1.id === attr.id),
            ) || null
          }
          onChange={(_, newValue) => {
            onChangeInput({
              target: {
                name: 'selectedAttribute1',
                value: newValue ? newValue : '',
              },
            } as React.ChangeEvent<HTMLInputElement>);
          }}
          renderOption={(props, option, { selected }) =>
            multiAutoCompleteMenu(props, selected, option.name)
          }
          renderInput={(params) => (
            <TextField
              {...params}
              name="selectedAttribute1"
              fullWidth
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
            />
          )}
          onPointerDown={(e) => e.stopPropagation()}
        />
      </TableCell>
      <TableCell sx={{ py: 0, px: 0.5 }}>
        <Autocomplete
          multiple
          size="small"
          sx={{ py: 1 }}
          disableCloseOnSelect
          options={options.attribute2MasterList}
          noOptionsText={t('MSG_NO_OPTION')}
          getOptionLabel={(option) => option.name ?? ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={
            options.attribute2MasterList.filter((attr) =>
              editItem.selectedAttribute2.some((attr2) => attr2.id === attr.id),
            ) || null
          }
          onChange={(_, newValue) => {
            onChangeInput({
              target: {
                name: 'selectedAttribute2',
                value: newValue ? newValue : '',
              },
            } as React.ChangeEvent<HTMLInputElement>);
          }}
          renderOption={(props, option, { selected }) =>
            multiAutoCompleteMenu(props, selected, option.name)
          }
          renderInput={(params) => (
            <TextField
              {...params}
              name="selectedAttribute2"
              fullWidth
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
            />
          )}
          onPointerDown={(e) => e.stopPropagation()}
        />
      </TableCell>
      <TableCell sx={{ py: 0, px: 0.5 }}>
        <Autocomplete
          multiple
          size="small"
          sx={{ py: 1 }}
          disableCloseOnSelect
          options={options.attribute3MasterList}
          noOptionsText={t('MSG_NO_OPTION')}
          getOptionLabel={(option) => option.name ?? ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={
            options.attribute3MasterList.filter((attr) =>
              editItem.selectedAttribute3.some((attr3) => attr3.id === attr.id),
            ) || null
          }
          onChange={(_, newValue) => {
            onChangeInput({
              target: {
                name: 'selectedAttribute3',
                value: newValue ? newValue : '',
              },
            } as React.ChangeEvent<HTMLInputElement>);
          }}
          renderOption={(props, option, { selected }) =>
            multiAutoCompleteMenu(props, selected, option.name)
          }
          renderInput={(params) => (
            <TextField
              {...params}
              name="selectedAttribute3"
              fullWidth
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
            />
          )}
          onPointerDown={(e) => e.stopPropagation()}
        />
      </TableCell>
      <TableCell sx={{ py: 0, px: 0.5 }}>
        <Autocomplete
          multiple
          size="small"
          sx={{ py: 1 }}
          disableCloseOnSelect
          options={options.attribute5MasterList}
          noOptionsText={t('MSG_NO_OPTION')}
          getOptionLabel={(option) => option.name ?? ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={
            options.attribute5MasterList.filter((attr) =>
              editItem.selectedAttribute5.some((attr5) => attr5.id === attr.id),
            ) || null
          }
          onChange={(_, newValue) => {
            onChangeInput({
              target: {
                name: 'selectedAttribute5',
                value: newValue ? newValue : '',
              },
            } as React.ChangeEvent<HTMLInputElement>);
          }}
          renderOption={(props, option, { selected }) =>
            multiAutoCompleteMenu(props, selected, option.name)
          }
          renderInput={(params) => (
            <TextField
              {...params}
              name="selectedAttribute5"
              fullWidth
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
            />
          )}
          onPointerDown={(e) => e.stopPropagation()}
        />
      </TableCell>
      <TableCell sx={{ py: 0, px: 0.5 }}>
        <Autocomplete
          multiple
          size="small"
          sx={{ py: 1 }}
          disableCloseOnSelect
          options={options.attribute6MasterList}
          noOptionsText={t('MSG_NO_OPTION')}
          getOptionLabel={(option) => option.name ?? ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={
            options.attribute6MasterList.filter((attr) =>
              editItem.selectedAttribute6.some((attr6) => attr6.id === attr.id),
            ) || null
          }
          onChange={(_, newValue) => {
            onChangeInput({
              target: {
                name: 'selectedAttribute6',
                value: newValue ? newValue : '',
              },
            } as React.ChangeEvent<HTMLInputElement>);
          }}
          renderOption={(props, option, { selected }) =>
            multiAutoCompleteMenu(props, selected, option.name)
          }
          renderInput={(params) => (
            <TextField
              {...params}
              name="selectedAttribute6"
              fullWidth
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
            />
          )}
          onPointerDown={(e) => e.stopPropagation()}
        />
      </TableCell>
      <TableCell sx={{ py: 0, px: 0.5 }}>
        <Autocomplete
          multiple
          size="small"
          sx={{ py: 1 }}
          disableCloseOnSelect
          options={options.brandMasterList}
          noOptionsText={t('MSG_NO_OPTION')}
          getOptionLabel={(option) => option.name ?? ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={
            options.brandMasterList.filter((attr) =>
              editItem.selectedBrandName.some((brand) => brand.id === attr.id),
            ) || null
          }
          onChange={(_, newValue) => {
            onChangeInput({
              target: {
                name: 'selectedBrandName',
                value: newValue ? newValue : '',
              },
            } as React.ChangeEvent<HTMLInputElement>);
          }}
          renderOption={(props, option, { selected }) =>
            multiAutoCompleteMenu(props, selected, option.name)
          }
          renderInput={(params) => (
            <TextField
              {...params}
              name="selectedBrandName"
              fullWidth
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
            />
          )}
          onPointerDown={(e) => e.stopPropagation()}
        />
      </TableCell>
      <TableCell sx={{ py: 0, px: 0.5 }}>
        <Select
          size="small"
          sx={{
            minWidth: '100px',
            bgcolor: isRequiredCondition ? 'warning.light' : '',
          }}
          value={editItem.selectedConditions}
          disabled={!isRequiredCondition}
          onChange={(e) =>
            onChangeInput({
              target: {
                name: 'selectedConditions',
                value: e.target.value,
              },
            } as React.ChangeEvent<HTMLInputElement>)
          }
          onPointerDown={(e) => e.stopPropagation()}
        >
          {Object.values(ITEM_CONDITIONS).map((con, index) => (
            <MenuItem key={index} value={con.value}>
              {con.label}
            </MenuItem>
          ))}
        </Select>
      </TableCell>
      <TableCell sx={{ py: 0, px: 0.5 }} colSpan={3} />
      <TableCell sx={{ py: 0, px: 0.5 }}>
        <TextField
          fullWidth
          size="small"
          value={editItem.memo}
          name="memo"
          sx={{ minWidth: '150px' }}
          onChange={(e) => onChangeInput(e)}
          onPointerDown={(e) => e.stopPropagation()}
          slotProps={{
            htmlInput: {
              maxLength: MaxLength.ManageInspectionItem.MEMO,
            },
          }}
        />
      </TableCell>
      <TableCell
        sx={[manageItemStyle.iconCellCenter, manageItemStyle.iconEditingSticky]}
        colSpan={3}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          <Button
            size="small"
            sx={[
              manageItemStyle.iconCellCenter,
              manageItemStyle.iconEditing,
              isDisabledSaveBtn || isDisabledBtn
                ? manageItemStyle.editDisabled
                : manageItemStyle.editOk,
            ]}
            disabled={isDisabledSaveBtn || isDisabledBtn}
            onClick={() => onClickOk(editItem)}
          >
            <CheckOutlined sx={{ fontSize: '16px' }} />
          </Button>
          <Button
            size="small"
            sx={[
              manageItemStyle.iconCellCenter,
              manageItemStyle.iconEditing,
              isDisabledBtn
                ? manageItemStyle.editDisabled
                : manageItemStyle.editCancel,
            ]}
            disabled={isDisabledBtn}
            onClick={onClickCancel}
          >
            <CloseOutlined sx={{ fontSize: '16px' }} />
          </Button>
        </Box>
      </TableCell>
    </React.Fragment>
  );
};

export default EditDepth2Row;
